import { useEffect } from 'react'
import { Button } from '../../catalyst/button'
import { Description, Field as FormField, FieldGroup, Label } from '../../catalyst/fieldset'
import { Input } from '../../catalyst/input'
import { Link } from '../../catalyst/link'
import { Text } from '../../catalyst/text'
import { api, dropNulls } from '../../lib/api'
import { areaInputValue, areaUnitLabel, formatArea } from '../../lib/area'
import { AssetNameInput } from '../../lib/asset-picker'
import { useLogNames } from '../../lib/farm-log-names'
import { InputPicker, type InputKind } from '../../lib/input-picker'
import { assetIdFor, useFarmAssets, useFarmStock } from '../../lib/inventory'
import { useEntryChecks, type LabelCheck, type LabelFinding } from '../../lib/canadagap'
import { usePesticideDetail } from '../../lib/pesticides'
import { amountFromRate } from '../../lib/rates'
import type { EquipmentSettings, FertilizerLog, Field, SprayLog } from '../../lib/types'
import { answerLabel, ChoiceButtons, SameAsLast, YesNo } from '../chips'
import { amountProblem, fieldValues, forField, optNum, optText, patchField, seasonYearOf, str, type FlowDef, type FlowEntry, type StepCtx } from '../flow'
import { blueberryUse, canSuggest, LabelFacts } from '../label-facts'
import type { Values } from '../use-draft'
import { namesUsed, rememberWho, WhoField } from '../who'
import { AmountInput, cap, placeOf } from './shared'
import { SuggestButton } from './spray'

// "Applied crop inputs": one entry for everything that went on a field in one pass, pesticides and
// fertilizers alike (a fungicide tank-mixed with a liquid feed, nutrients through the drip). On
// save, each product goes to the form it belongs on: pesticides to one Form H1 spray log per field,
// each fertilizer to its own Form H2 log per field.

const METHODS = ['ground', 'air', 'foliar', 'drip', 'furrow', 'seed', 'other'] as const
const METHOD_LABEL: Record<string, string> = { drip: 'Drip / fertigation' }
const methodLabel = (m: string) => METHOD_LABEL[m] ?? cap(m)

export type CropInput = {
  /** Which form its record goes on. Blank until picked from a list or chosen by the grower. */
  kind: InputKind | ''
  productName: string
  /** A registered pesticide (registry id and PCP number), when picked from the registry or stock. */
  productId: string
  pcpNumber: string
  activeIngredients: string
  /** The farm's stock item, when picked from it. */
  stockItemId: string
  rateApplied: string
  rateUnit: string
  /** Amounts the grower typed, per field. A field without one uses rate × its area. */
  amounts: Record<string, { quantity: string; unit: string }>
  /** Pesticides: the PHI from the label. */
  phiDays: string
  /** Pesticides, H1: applied following the label. Unanswered until the grower says. */
  labelFollowed?: boolean
  /** "Yes" was answered for the grower because every label check passed; taken back if one stops passing. */
  labelAuto?: boolean
  /** Fertilizers, H2: the lot, if there is one. */
  lotNumber: string
}

export const emptyInput = (): CropInput => ({
  kind: '',
  productName: '',
  productId: '',
  pcpNumber: '',
  activeIngredients: '',
  stockItemId: '',
  rateApplied: '',
  rateUnit: 'L/ha',
  amounts: {},
  phiDays: '',
  lotNumber: '',
})

export const inputsOf = (v: Values): CropInput[] => (Array.isArray(v.inputs) ? (v.inputs as CropInput[]) : [emptyInput()])
const named = (v: Values) => inputsOf(v).filter((i) => i.productName.trim() !== '')

/** The area treated on one field: what the grower typed, else the whole field. */
export function areaOn(values: Values, field: Field, areaUnit: StepCtx['areaUnit']): number | undefined {
  const typed = fieldValues(values, field.id).areaTreated
  return typed === undefined ? Number(areaInputValue(field.areaM2, areaUnit)) : optNum(typed)
}

/** How much of a pesticide went on one field: what was typed there, else rate × that field's area. */
export function amountOn(input: CropInput, values: Values, field: Field, areaUnit: StepCtx['areaUnit']): { quantity: number; unit: string; typed: boolean } | null {
  const typed = input.amounts?.[field.id]
  if (typed && typed.quantity.trim() !== '') return { quantity: Number(typed.quantity), unit: typed.unit, typed: true }
  const rate = optNum(input.rateApplied)
  const area = areaOn(values, field, areaUnit)
  if (rate == null || area == null) return null
  const c = amountFromRate(rate, input.rateUnit, area, areaUnit)
  return c ? { ...c, typed: false } : null
}

function setAmount(input: CropInput, fieldId: string, amount: { quantity: string; unit: string } | null): CropInput {
  const amounts = { ...input.amounts }
  if (amount) amounts[fieldId] = amount
  else delete amounts[fieldId]
  return { ...input, amounts }
}

const settingsOf = (v: Values): EquipmentSettings => (v.settings && typeof v.settings === 'object' ? (v.settings as EquipmentSettings) : {})

// ---------------------------------------------------------------------------------------
// What did you apply?
// ---------------------------------------------------------------------------------------

/** How much went on each field: one amount for one field, a row per field for several. */
function Amounts({ input, update, ctx }: { input: CropInput; update: (next: CropInput) => void; ctx: StepCtx }) {
  const { values, areaUnit, selectedFields } = ctx
  const several = selectedFields.length > 1
  return (
    <div className="space-y-3">
      {several && <p className="text-base/6 font-medium text-zinc-950 sm:text-sm/6 dark:text-white">Amount used on each field</p>}
      {selectedFields.map((f) => {
        const amount = amountOn(input, values, f, areaUnit)
        const typed = input.amounts?.[f.id]
        const fromRate = typed ? amountOn({ ...input, amounts: {} }, values, f, areaUnit) : null
        return (
          <div key={f.id}>
            <div className="flex items-end gap-3">
              <FormField className="flex-1">
                <Label>{several ? `${f.name} · ${formatArea(f.areaM2, areaUnit)}` : 'Amount used'}</Label>
                <AmountInput
                  value={typed ? typed.quantity : amount ? String(amount.quantity) : ''}
                  onChange={(q) => update(setAmount(input, f.id, { quantity: q, unit: typed?.unit ?? amount?.unit ?? 'L' }))}
                />
              </FormField>
              <FormField className="w-24">
                <Label>Unit</Label>
                <Input value={typed?.unit ?? amount?.unit ?? 'L'} onChange={(e) => update(setAmount(input, f.id, { quantity: typed?.quantity ?? String(amount?.quantity ?? ''), unit: e.target.value }))} />
              </FormField>
            </div>
            {amount && !amount.typed ? (
              <Text className="mt-1">
                = {input.rateApplied} {input.rateUnit} × {areaOn(values, f, areaUnit)} {areaUnitLabel(areaUnit)}.{!several && ' Change it if you used a different amount.'}
              </Text>
            ) : fromRate ? (
              <div className="mt-1">
                <SuggestButton onClick={() => update(setAmount(input, f.id, null))}>Work it out from the rate</SuggestButton>
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

/** A pesticide: what its label says, the rate, the amount on each field, the PHI and the label question. */
const RATE_CODES = new Set(['rate_above_label', 'rate_below_label'])

function Findings({ findings }: { findings: LabelFinding[] }) {
  if (findings.length === 0) return null
  return (
    <div className="-mt-2 space-y-1">
      {findings.map((f) => (
        <p
          key={f.message}
          className={
            f.tone === 'violation'
              ? 'rounded-lg bg-red-50 p-3 text-sm/6 text-red-800 dark:bg-red-500/10 dark:text-red-300'
              : 'rounded-lg bg-amber-50 p-3 text-sm/6 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200'
          }
        >
          {f.tone === 'violation' ? '⚠ ' : ''}
          {f.message}
        </p>
      ))}
    </div>
  )
}

/**
 * "Did you follow the label?" Answered for the grower when every label check passed (rate, PHI,
 * applications this season, re-treatment interval), still changeable; asked otherwise. The label
 * covers more than we can check, so the answer says what was checked.
 */
function LabelQuestion({ input, update, check }: { input: CropInput; update: (next: CropInput) => void; check: LabelCheck | null }) {
  if (input.labelAuto && input.labelFollowed === true) {
    return (
      <div className="flex items-start justify-between gap-3 rounded-lg bg-emerald-50 p-3 text-sm/6 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
        <span>
          <span className="font-medium">✓ Label followed:</span> the rate, PHI and applications this season match the checked label. Answered for you.
        </span>
        <button type="button" className="min-h-11 shrink-0 font-medium" onClick={() => update({ ...input, labelAuto: false })}>
          Change
        </button>
      </div>
    )
  }
  return (
    <div>
      <YesNo label="Did you follow the label instructions?" value={input.labelFollowed} onChange={(a) => update({ ...input, labelFollowed: a, labelAuto: false })} />
      {check?.status === 'unknown' && check.reason && <Text className="mt-2">{check.reason} So it is up to you.</Text>}
    </div>
  )
}

function PesticideFields({ input, update, ctx, check }: { input: CropInput; update: (next: CropInput) => void; ctx: StepCtx; check: LabelCheck | null }) {
  const findings = check?.findings ?? []
  const { values, patch } = ctx
  const detail = usePesticideDetail(input.productId || undefined)
  const label = detail.pesticide?.label.status === 'ready' ? detail.pesticide.label : null
  const use = label ? blueberryUse(label) : null
  const suggest = !!label && canSuggest(label) && !!use

  // A checked label that names one application method fills it in, until the grower picks one.
  const labelMethod = suggest ? use.method : null
  useEffect(() => {
    if (labelMethod && values.method === undefined) patch({ method: labelMethod, methodFromLabel: true })
  }, [labelMethod, values.method, patch])

  return (
    <>
      {input.productId && <LabelFacts detail={detail} />}
      <div className="flex gap-3">
        <FormField className="flex-1">
          <Label>Rate</Label>
          <AmountInput value={input.rateApplied} onChange={(r) => update({ ...input, rateApplied: r })} />
        </FormField>
        <FormField className="w-28">
          <Label>Per</Label>
          <Input value={input.rateUnit} onChange={(e) => update({ ...input, rateUnit: e.target.value })} />
        </FormField>
      </div>
      {suggest && use.rates.length > 0 && (
        <div className="-mt-2 flex flex-wrap gap-2">
          {use.rates.slice(0, 4).map((r) => (
            <SuggestButton key={`${r.value} ${r.unit}`} onClick={() => update({ ...input, rateApplied: String(r.value), rateUnit: r.unit })}>
              Use {r.value} {r.unit} (label)
            </SuggestButton>
          ))}
        </div>
      )}
      <Findings findings={findings.filter((f) => RATE_CODES.has(f.code))} />
      <Amounts input={input} update={update} ctx={ctx} />
      <FormField>
        <Label>Pre-harvest interval (PHI), days</Label>
        <Description>From the label. Sets the earliest date you can pick these fields.</Description>
        <Input type="number" inputMode="numeric" min="0" step="1" value={input.phiDays} onChange={(e) => update({ ...input, phiDays: e.target.value })} />
      </FormField>
      {suggest && use.phi && String(use.phi.max) !== input.phiDays && (
        <div className="-mt-2">
          {/* Labels can give different PHIs by target; the longest is the one that is safe for all of them. */}
          <SuggestButton onClick={() => update({ ...input, phiDays: String(use.phi!.max) })}>
            Use {use.phi.max} day{use.phi.max === 1 ? '' : 's'} ({use.phi.min === use.phi.max ? 'label' : `longest on the label, ${use.phi.min}–${use.phi.max} by target`})
          </SuggestButton>
        </div>
      )}
      <Findings findings={findings.filter((f) => !RATE_CODES.has(f.code))} />
      <LabelQuestion input={input} update={update} check={check} />
    </>
  )
}

/** A fertilizer (or other input, such as a drip-line cleaner): Form H2 asks the blend, rate and lot. */
function FertilizerFields({ input, update, ctx }: { input: CropInput; update: (next: CropInput) => void; ctx: StepCtx }) {
  return (
    <>
      <div className="flex gap-3">
        <FormField className="flex-1">
          <Label>Rate</Label>
          <AmountInput value={input.rateApplied} onChange={(r) => update({ ...input, rateApplied: r })} />
        </FormField>
        <FormField className="w-28">
          <Label>Per</Label>
          <Input value={input.rateUnit} onChange={(e) => update({ ...input, rateUnit: e.target.value })} />
        </FormField>
      </div>
      <FormField>
        <Label>Lot number (optional)</Label>
        <Input value={input.lotNumber} onChange={(e) => update({ ...input, lotNumber: e.target.value })} autoComplete="off" />
      </FormField>
      {!input.stockItemId && (
        <Text>
          Use it often? <Link href={`/farms/${ctx.farmId}/inventory`}>Add it to your farm stock</Link> and it is one tap next time.
        </Text>
      )}
    </>
  )
}

const KIND_OPTIONS = [
  { value: 'pesticide', label: 'Pesticide (Form H1)' },
  { value: 'fertilizer', label: 'Fertilizer or other input (Form H2)' },
] as const

function InputRow({
  input,
  i,
  inputs,
  ctx,
  stock,
  blends,
  check,
}: {
  input: CropInput
  i: number
  inputs: CropInput[]
  ctx: StepCtx
  stock: ReturnType<typeof useFarmStock>['items']
  blends: string[]
  check: LabelCheck | null
}) {
  const { patch } = ctx
  const update = (next: CropInput) => patch({ inputs: inputs.map((x, idx) => (idx === i ? next : x)) })
  const typedOnly = !input.stockItemId && !input.productId

  return (
    <fieldset className="space-y-4 rounded-lg p-4 ring-1 ring-zinc-950/10 dark:ring-white/10">
      <FormField>
        <Label>Product</Label>
        <InputPicker
          name={input.productName}
          picked={input.stockItemId || input.productId ? input : null}
          stock={stock}
          pastBlends={blends}
          // Typing again unlinks a picked product: the name no longer matches it.
          onName={(name) => update({ ...input, productName: name, productId: '', pcpNumber: '', activeIngredients: '', stockItemId: '' })}
          onPick={(picked) => update({ ...input, ...picked, kind: picked.kind || input.kind })}
        />
      </FormField>
      {input.productName.trim() !== '' && (typedOnly || !input.kind) && (
        <ChoiceButtons label="What is it?" value={input.kind} options={KIND_OPTIONS} onChange={(kind) => update({ ...input, kind })} />
      )}
      {input.kind === 'pesticide' && <PesticideFields input={input} update={update} ctx={ctx} check={check} />}
      {input.kind === 'fertilizer' && <FertilizerFields input={input} update={update} ctx={ctx} />}
      {inputs.length > 1 && (
        <Button plain onClick={() => patch({ inputs: inputs.filter((_, idx) => idx !== i) })}>
          Remove this product
        </Button>
      )}
    </fieldset>
  )
}

/** The area treated on each field: the whole field unless the grower says less. */
function Areas({ ctx }: { ctx: StepCtx }) {
  const { values, areaUnit, selectedFields } = ctx
  const several = selectedFields.length > 1
  return (
    <div className="space-y-3">
      {several && <p className="text-base/6 font-medium text-zinc-950 sm:text-sm/6 dark:text-white">Area treated ({areaUnitLabel(areaUnit)})</p>}
      {selectedFields.map((f) => {
        const typed = fieldValues(values, f.id).areaTreated
        return (
          <FormField key={f.id}>
            <Label>{several ? f.name : `Area treated (${areaUnitLabel(areaUnit)})`}</Label>
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              value={typed === undefined ? areaInputValue(f.areaM2, areaUnit) : str(typed)}
              onChange={(e) => patchField(ctx, f.id, { areaTreated: e.target.value })}
            />
          </FormField>
        )
      })}
    </div>
  )
}

/**
 * The "label followed" answers after a label check: "Yes" for an unanswered pesticide whose checks
 * all pass, and our own "Yes" taken back from one whose checks no longer pass. The grower's own
 * answers are never touched. The same array when nothing changes.
 */
export function answerFromLabel(inputs: CropInput[], checks: (LabelCheck | null)[]): CropInput[] {
  const next = inputs.map((input, n) => {
    const status = checks[n]?.status
    if (input.kind !== 'pesticide' || !status) return input
    if (status === 'pass' && input.labelFollowed === undefined) return { ...input, labelFollowed: true, labelAuto: true }
    if (status !== 'pass' && input.labelAuto) return { ...input, labelFollowed: undefined, labelAuto: false }
    return input
  })
  return next.some((x, n) => x !== inputs[n]) ? next : inputs
}

/**
 * Each input's label check across the chosen fields (the season's earlier applications differ by
 * field): a pass only when it passes on every field. Null while checking, and for non-pesticides.
 */
function useLabelChecks(ctx: StepCtx): (LabelCheck | null)[] {
  const { orgId, farmId, values, selectedFields } = ctx
  const inputs = inputsOf(values)
  const pesticides = inputs.filter((i) => i.kind === 'pesticide' && i.productName.trim() !== '')
  const requests = pesticides.length
    ? selectedFields.flatMap((f) => fieldEntries(forField(ctx, f)).filter((e) => e.kind === 'spray').map((e) => ({ form: 'h1' as const, entry: e.body })))
    : []
  const results = useEntryChecks(pesticides.length ? orgId : undefined, farmId || undefined, requests)
  return inputs.map((input) => {
    const k = pesticides.indexOf(input)
    if (k < 0 || !results || results.length !== requests.length) return null
    const perField = results.map((r) => r?.labelChecks?.[k] ?? null)
    if (perField.some((c) => c === null)) return null
    const checks = perField as LabelCheck[]
    const findings = [...new Map(checks.flatMap((c, n) => c.findings.map((f) => [f.message, selectedFields.length > 1 && !RATE_CODES.has(f.code) ? { ...f, message: `${selectedFields[n]!.name}: ${f.message}` } : f]))).values()]
    const status = checks.some((c) => c.status === 'fail') ? 'fail' : checks.every((c) => c.status === 'pass') ? 'pass' : 'unknown'
    return { status, findings, reason: checks.find((c) => c.status === 'unknown')?.reason }
  })
}

function ApplyStep(ctx: StepCtx) {
  const { orgId, farmId, values, patch, last } = ctx
  const inputs = inputsOf(values)
  const { items: stock } = useFarmStock(orgId, farmId)
  const { names: blends } = useLogNames(orgId, farmId, 'blend')
  const lastSpray = last as SprayLog | null
  const checks = useLabelChecks(ctx)

  // Answer "label followed" for a product whose checks all pass and that the grower hasn't answered;
  // take it back when they stop passing, so a "Yes" we gave never outlives the facts behind it. One
  // effect for every product, so two updating at once can't overwrite each other.
  const statuses = checks.map((c) => c?.status ?? 'pending').join(',')
  useEffect(() => {
    const next = answerFromLabel(inputs, checks)
    if (next !== inputs) patch({ inputs: next })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statuses])

  return (
    <FieldGroup>
      {lastSpray?.products?.length ? (
        <SameAsLast
          label={lastSpray.products.map((p) => p.productName).join(', ')}
          onClick={() =>
            patch({
              inputs: lastSpray.products!.map((p) => ({
                ...emptyInput(),
                kind: 'pesticide' as const,
                productName: p.productName,
                productId: p.productId ?? '',
                pcpNumber: p.pcpNumber ?? '',
                rateApplied: p.rateApplied == null ? '' : String(p.rateApplied),
                rateUnit: p.rateUnit ?? 'L/ha',
                phiDays: p.phiDays == null ? '' : String(p.phiDays),
                // Without a rate, last time's amount stands in for each field (it can't be worked out).
                amounts: p.rateApplied == null ? Object.fromEntries(ctx.selectedFields.map((f) => [f.id, { quantity: String(p.quantityUsed), unit: p.quantityUnit }])) : {},
              })),
            })
          }
        />
      ) : null}
      <Areas ctx={ctx} />
      {inputs.map((input, i) => (
        <InputRow key={i} input={input} i={i} inputs={inputs} ctx={ctx} stock={stock} blends={blends} check={checks[i] ?? null} />
      ))}
      <Button outline className="w-full" onClick={() => patch({ inputs: [...inputs, emptyInput()] })}>
        + Add another product
      </Button>
      {inputs.filter((i) => i.kind === 'pesticide').length > 1 && (
        <Text>Mixing products? Do a jar test first, and follow each label’s mixing directions.</Text>
      )}
    </FieldGroup>
  )
}

/** Every named product is ready to save: it has a form, and a pesticide has an amount on every field. */
export function inputsReady(values: Values, ctx?: StepCtx): boolean {
  const list = named(values)
  if (list.length === 0) return false
  const fields = ctx?.selectedFields ?? []
  return list.every((i) => {
    if (amountProblem(i.rateApplied) || Object.values(i.amounts ?? {}).some((a) => amountProblem(a.quantity))) return false
    if (i.kind === 'fertilizer') return true
    if (i.kind !== 'pesticide') return false
    return fields.every((f) => {
      const a = amountOn(i, values, f, ctx!.areaUnit)
      return !!a && a.quantity > 0 && !amountProblem(String(a.quantity)) && a.unit.trim() !== ''
    })
  })
}

// ---------------------------------------------------------------------------------------
// How was it applied?
// ---------------------------------------------------------------------------------------

/** The newest application made with this equipment, for its settings. */
async function lastSettings(orgId: string, farmId: string, assetId: string): Promise<EquipmentSettings | null> {
  const base = `/orgs/${orgId}/farms/${farmId}`
  const [spray, fertilizer] = await Promise.all([
    api.getPage<SprayLog>(`${base}/spray-logs?assetId=${assetId}&limit=1`),
    api.getPage<FertilizerLog>(`${base}/fertilizer-logs?assetId=${assetId}&limit=1`),
  ])
  const found = [spray.data[0], fertilizer.data[0]].filter((l): l is SprayLog | FertilizerLog => !!l?.equipmentSettings)
  found.sort((a, b) => b.appliedOn.localeCompare(a.appliedOn))
  return found[0]?.equipmentSettings ?? null
}

function EquipmentFields({ ctx }: { ctx: StepCtx }) {
  const { orgId, farmId, values, patch } = ctx
  const { assets } = useFarmAssets(orgId, farmId, 'EQUIPMENT')
  const { names } = useLogNames(orgId, farmId, 'equipment')
  const assetId = optText(values.assetId) ?? null
  const settings = settingsOf(values)
  const kinds = new Set(named(values).map((i) => i.kind))
  const drip = values.method === 'drip'

  // Picking saved equipment fills in how it was set last time, unless something is already typed.
  useEffect(() => {
    if (!assetId || Object.values(settingsOf(values)).some(Boolean)) return
    let cancelled = false
    lastSettings(orgId, farmId, assetId).then(
      (found) => {
        if (!cancelled && found) patch({ settings: found, settingsFromLast: true })
      },
      () => {},
    )
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, farmId, assetId])

  const setting = (key: keyof EquipmentSettings, label: string, placeholder: string) => (
    <FormField>
      <Label>{label}</Label>
      <Input value={settings[key] ?? ''} placeholder={placeholder} onChange={(e) => patch({ settings: { ...settings, [key]: e.target.value }, settingsFromLast: false })} autoComplete="off" />
    </FormField>
  )

  return (
    <div className="space-y-4">
      <FormField>
        <Label>Equipment (optional)</Label>
        <AssetNameInput
          assets={assets}
          pastNames={names}
          linkedAssetId={assetId}
          inventoryHref={`/farms/${farmId}/inventory`}
          value={str(values.equipmentName)}
          onChange={(text) => {
            const id = assetIdFor(assets, text)
            // Different equipment: its own settings, not the last one's.
            patch({ equipmentName: text, assetId: id, ...(id !== assetId ? { settings: {}, settingsFromLast: false } : {}) })
          }}
        />
      </FormField>
      <details className="group rounded-lg px-4 py-3 ring-1 ring-zinc-950/10 dark:ring-white/10" open={Object.values(settings).some(Boolean) || undefined}>
        <summary className="cursor-pointer list-none text-sm/6 font-medium text-zinc-950 select-none dark:text-white">Settings (optional)</summary>
        <div className="mt-4 space-y-4">
          {values.settingsFromLast === true && <Text>From the last time this equipment was used. Change anything that was different.</Text>}
          {(kinds.has('pesticide') || drip) && (
            <>
              {setting('nozzle', 'Nozzles', 'e.g. TeeJet 8002')}
              {setting('pressure', 'Pressure', 'e.g. 150 psi')}
            </>
          )}
          {!drip && setting('speed', 'Speed', 'e.g. 4 km/h')}
          {kinds.has('fertilizer') && !drip && setting('spreaderSetting', 'Spreader setting', 'e.g. gate 12')}
        </div>
      </details>
    </div>
  )
}

function HowStep(ctx: StepCtx) {
  const { values, patch, last } = ctx
  const lastSpray = last as SprayLog | null
  const pesticides = named(values).some((i) => i.kind === 'pesticide')
  // No method from a label: start from ground, the most common.
  useEffect(() => {
    if (values.method === undefined) patch({ method: 'ground' })
  }, [values.method, patch])

  return (
    <FieldGroup>
      {lastSpray?.applicatorName || lastSpray?.applicationMethod ? (
        <SameAsLast
          label={[methodLabel(lastSpray.applicationMethod), lastSpray.equipmentName, lastSpray.applicatorName].filter(Boolean).join(', ')}
          onClick={() =>
            patch({
              method: lastSpray.applicationMethod,
              methodFromLabel: false,
              applicator: lastSpray.applicatorName ?? '',
              equipmentName: lastSpray.equipmentName ?? '',
              assetId: lastSpray.assetId,
              settings: lastSpray.equipmentSettings ?? {},
            })
          }
        />
      ) : null}
      <div>
        <ChoiceButtons
          label="How was it applied?"
          value={str(values.method) as (typeof METHODS)[number] | ''}
          options={METHODS.map((m) => ({ value: m, label: methodLabel(m) }))}
          onChange={(m) => patch({ method: m, methodFromLabel: false })}
        />
        {values.methodFromLabel === true && <Text className="mt-2">From the label. Change it if you applied it another way.</Text>}
      </div>
      {values.method === 'drip' && (
        <FormField>
          <Label>How long did it run? (minutes, optional)</Label>
          <Description>Nutrients or a cleaner through the drip. Watering with nothing added is logged as field work.</Description>
          <Input type="number" inputMode="numeric" min="1" step="1" value={str(values.durationMinutes)} onChange={(e) => patch({ durationMinutes: e.target.value })} />
        </FormField>
      )}
      <EquipmentFields ctx={ctx} />
      {pesticides && (
        <FormField>
          <Label>Weather</Label>
          <Input value={str(values.weather)} onChange={(e) => patch({ weather: e.target.value })} placeholder="Clear, 15°C, light wind" />
        </FormField>
      )}
      <WhoField ctx={ctx} />
    </FieldGroup>
  )
}

// ---------------------------------------------------------------------------------------
// The flow
// ---------------------------------------------------------------------------------------

/** What every record from this entry shares: the date, how, with what, who. */
function shared(values: Values) {
  const date = str(values.date)
  const method = str(values.method)
  const duration = method === 'drip' ? optNum(values.durationMinutes) : undefined
  const settings = Object.fromEntries(Object.entries(settingsOf(values)).filter(([, v]) => typeof v === 'string' && v.trim() !== '').map(([k, v]) => [k, v.trim()]))
  return {
    seasonYear: seasonYearOf(date),
    appliedOn: date,
    applicationMethod: method,
    durationMinutes: duration != null && Number.isInteger(duration) && duration > 0 ? duration : null,
    assetId: optText(values.assetId) ?? null,
    equipmentName: optText(values.equipmentName) ?? null,
    equipmentSettings: Object.keys(settings).length ? settings : null,
    applicatorName: optText(values.applicator) ?? null,
  }
}

const H1 = { framework: 'CanadaGAP', formCode: 'H1', formName: 'Agronomic Inputs (Agricultural Chemicals)' }
const H2 = { framework: 'CanadaGAP', formCode: 'H2', formName: 'Agronomic Inputs (Fertilizers)' }

/** One field's records: a spray log with every pesticide, and a fertilizer log per fertilizer. */
function fieldEntries(ctx: StepCtx): FlowEntry[] {
  const { values, field, areaUnit, farmId } = ctx
  if (!field) return []
  const common = { fieldId: field.id, ...shared(values) }
  const list = named(values)
  const pesticides = list.filter((i) => i.kind === 'pesticide')
  const fertilizers = list.filter((i) => i.kind === 'fertilizer')
  const area = areaOn(values, field, areaUnit)
  const entries: FlowEntry[] = []
  if (pesticides.length > 0) {
    entries.push({
      kind: 'spray',
      checkForm: 'h1',
      badge: H1,
      fieldId: field.id,
      historyPath: `/farms/${farmId}/fields/${field.id}/spray-logs`,
      body: dropNulls({
        ...common,
        areaTreated: area ?? null,
        areaUnit: area == null ? null : areaUnit,
        weatherConditions: optText(values.weather) ?? null,
        products: pesticides.map((p) => {
          const amount = amountOn(p, values, field, areaUnit)
          const rate = optNum(p.rateApplied)
          return {
            productName: p.productName.trim(),
            productId: p.productId || undefined,
            pcpNumber: p.pcpNumber || undefined,
            quantityUsed: amount?.quantity ?? 0,
            quantityUnit: amount?.unit.trim() || 'L',
            rateApplied: rate,
            rateUnit: rate == null ? undefined : optText(p.rateUnit),
            // Only the PHI: the server derives the EAHD from it and the application date.
            phiDays: optNum(p.phiDays),
            // Unanswered stays unset, so the record shows it as missing rather than as "No".
            labelFollowed: p.labelFollowed,
          }
        }),
      }),
    })
  }
  for (const f of fertilizers) {
    const rate = optNum(f.rateApplied)
    entries.push({
      kind: 'fertilizer',
      checkForm: 'h2Fertilizer',
      badge: H2,
      fieldId: field.id,
      historyPath: `/farms/${farmId}/fields/${field.id}/fertilizer-logs`,
      body: dropNulls({ ...common, blend: f.productName.trim(), rateAmount: rate ?? null, rateUnit: rate == null ? null : (optText(f.rateUnit) ?? null), lotNumber: optText(f.lotNumber) ?? null }),
    })
  }
  return entries
}

async function fetchLastSpray(orgId: string, farmId: string, fieldId: string | undefined): Promise<SprayLog | null> {
  if (!fieldId) return null
  const url = `/orgs/${orgId}/farms/${farmId}/spray-logs`
  const year = new Date().getFullYear()
  for (const seasonYear of [year, year - 1]) {
    const { data } = await api.getPage<SprayLog>(`${url}?fieldId=${fieldId}&seasonYear=${seasonYear}&limit=1`)
    if (data[0]) return api.get<SprayLog>(`${url}/${data[0].id}`)
  }
  return null
}

export const cropInputsFlow: FlowDef = {
  kind: 'crop_inputs',
  label: 'Applied crop inputs',
  needsField: true,
  severalFields: true,
  // The method starts unset so a checked label can fill it in; HowStep falls back to ground.
  initial: ({ today, fieldId }) => ({ fieldId, date: today, inputs: [emptyInput()] }),
  fetchLast: fetchLastSpray,
  steps: [
    {
      id: 'what',
      title: 'What did you apply?',
      // A product with an amount or rate that can't be saved holds the step, rather than being dropped.
      valid: inputsReady,
      render: (ctx) => <ApplyStep {...ctx} />,
    },
    {
      id: 'how',
      title: 'How was it applied?',
      valid: (v) => str(v.method) !== '' && (v.method !== 'drip' || !optText(v.durationMinutes) || (Number.isInteger(Number(v.durationMinutes)) && Number(v.durationMinutes) > 0)),
      render: (ctx) => <HowStep {...ctx} />,
    },
  ],
  review: (ctx) => {
    const { values, selectedFields, areaUnit } = ctx
    const totalArea = selectedFields.reduce((sum, f) => sum + (areaOn(values, f, areaUnit) ?? 0), 0)
    const total = (i: CropInput) => {
      const each = selectedFields.map((f) => amountOn(i, values, f, areaUnit))
      const units = [...new Set(each.map((a) => a?.unit))]
      if (each.some((a) => !a) || units.length !== 1) return each.map((a, n) => `${selectedFields[n]!.name} ${a ? `${a.quantity} ${a.unit}` : '?'}`).join(', ')
      return `${Number(each.reduce((s, a) => s + a!.quantity, 0).toFixed(4))} ${units[0]}`
    }
    const s = shared(values)
    return {
      title: `Applied crop inputs${placeOf(ctx)}`,
      lines: [
        { label: 'Date', value: str(values.date) },
        ...named(values).map((i) => ({
          label: i.productName,
          value:
            i.kind === 'pesticide'
              ? [`H1 pesticide`, total(i), i.rateApplied && `${i.rateApplied} ${i.rateUnit}`, i.phiDays && `PHI ${i.phiDays} day${i.phiDays === '1' ? '' : 's'}`, `label followed: ${answerLabel(i.labelFollowed).toLowerCase()}`]
                  .filter(Boolean)
                  .join(', ')
              : ['H2 fertilizer', i.rateApplied && `${i.rateApplied} ${i.rateUnit}`, i.lotNumber && `lot ${i.lotNumber}`].filter(Boolean).join(', '),
        })),
        { label: 'Method', value: [methodLabel(str(values.method)), s.durationMinutes && `${s.durationMinutes} min`].filter(Boolean).join(', ') },
        { label: 'Area', value: `${Number(totalArea.toFixed(2))} ${areaUnitLabel(areaUnit)}` },
        ...(s.equipmentName ? [{ label: 'Equipment', value: [s.equipmentName, ...Object.values(s.equipmentSettings ?? {})].join(', ') }] : []),
        ...(optText(values.weather) && named(values).some((i) => i.kind === 'pesticide') ? [{ label: 'Weather', value: str(values.weather) }] : []),
        ...(namesUsed(values).length ? [{ label: 'Applied by', value: [...new Set(namesUsed(values))].join(', ') }] : []),
      ],
    }
  },
  entry: () => ({}),
  entries: fieldEntries,
  historyPath: (farmId, fieldId) => `/farms/${farmId}/fields/${fieldId}/spray-logs`,
  onRecorded: rememberWho(),
}
